import { useEffect, useState, useCallback } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import api, { fmtDate, formatApiErrorDetail } from "../lib/api";
import { PriorityBadge } from "../components/badges";

const STATUS_COLORS = {
  pending: "text-neutral-400",
  dispatched: "text-amber-300",
  queued: "text-amber-300",
  expired: "text-neutral-500",
  in_progress: "text-cyan-300",
  completed: "text-[#00E676]",
  failed: "text-red-400",
  dispatch_failed: "text-red-400",
};

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ destination: "", action: "", priority: "normal", parameters: "{}" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/tasks").then(({ data }) => setTasks(data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    let params;
    try {
      params = JSON.parse(form.parameters || "{}");
    } catch {
      toast.error("Les paramètres doivent être un JSON valide");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/tasks", { ...form, parameters: params });
      setOpen(false);
      setForm({ destination: "", action: "", priority: "normal", parameters: "{}" });
      data.dispatch?.delivered
        ? toast.success(`Tâche ${data.task.task_id} envoyée à ${form.destination.toUpperCase()}`)
        : toast.error(`Tâche créée mais ${form.destination.toUpperCase()} est injoignable`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full border border-border bg-[#050505] px-3 py-2 font-mono text-sm text-white outline-none transition-colors duration-150 focus:border-primary";

  return (
    <div data-testid="tasks-page" className="relative space-y-6">
      <div aria-hidden="true" className="pointer-events-none absolute -top-28 left-1/3 h-64 w-[44rem] -translate-x-1/2 rounded-full bg-[#00E5FF]/[0.05] blur-3xl" />
      <div className="relative flex items-end justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-white">Tâches</h1>
          <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">Missions envoyées aux projets</p>
        </div>
        <button data-testid="new-task-button" onClick={() => setOpen(true)}
          className="flex items-center gap-2 bg-primary px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black shadow-[0_0_18px_rgba(0,229,255,0.25)] transition-opacity duration-150 hover:opacity-90">
          <Plus size={14} /> Nouvelle tâche
        </button>
      </div>

      <div className="overflow-x-auto border border-white/10 bg-card shadow-[0_0_20px_rgba(0,229,255,0.04)]">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-border font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              <th className="px-4 py-3">Task ID</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Destination</th>
              <th className="px-4 py-3">Priorité</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3">Créée</th>
              <th className="px-4 py-3">Résultat</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {tasks.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center font-mono text-xs text-neutral-600">Aucune tâche</td></tr>
            )}
            {tasks.map((t) => (
              <tr key={t.task_id} data-testid={`task-row-${t.task_id}`} className="transition-colors duration-100 hover:bg-muted/50">
                <td className="px-4 py-3 font-mono text-xs text-primary">{t.task_id}</td>
                <td className="px-4 py-3 font-mono text-xs text-white">{t.action}</td>
                <td className="px-4 py-3 font-mono text-xs uppercase text-neutral-300">{t.destination}</td>
                <td className="px-4 py-3"><PriorityBadge priority={t.priority} /></td>
                <td className={`px-4 py-3 font-mono text-xs uppercase ${STATUS_COLORS[t.status] || "text-neutral-400"}`} data-testid={`task-status-${t.task_id}`}>{t.status}</td>
                <td className="px-4 py-3 font-mono text-[11px] text-neutral-500">{fmtDate(t.created_at)}</td>
                <td className="max-w-[220px] truncate px-4 py-3 font-mono text-[10px] text-neutral-500">
                  {t.result ? `${t.result.result_id || ""} ${t.result.score ? `· score ${t.result.score}` : ""}` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-primary/25 bg-[#0f0f0f] text-white shadow-[0_0_40px_rgba(0,229,255,0.1)] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-widest">NOUVELLE TÂCHE</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Projet destinataire</label>
              <select data-testid="task-destination-select" required value={form.destination}
                onChange={(e) => setForm({ ...form, destination: e.target.value })} className={inputCls}>
                <option value="">— Sélectionner —</option>
                {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Action</label>
              <input data-testid="task-action-input" required value={form.action}
                onChange={(e) => setForm({ ...form, action: e.target.value })} className={inputCls} placeholder="CREATE_VIDEO" />
            </div>
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Priorité</label>
              <select data-testid="task-priority-select" value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })} className={inputCls}>
                <option value="low">low</option>
                <option value="normal">normal</option>
                <option value="high">high</option>
                <option value="critical">critical</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Paramètres (JSON)</label>
              <textarea data-testid="task-parameters-input" rows={4} value={form.parameters}
                onChange={(e) => setForm({ ...form, parameters: e.target.value })} className={inputCls}
                placeholder='{"duration": "2h", "theme": "rain"}' />
            </div>
            <button data-testid="task-submit-button" type="submit" disabled={saving}
              className="w-full bg-primary py-2.5 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black transition-opacity duration-150 hover:opacity-90 disabled:opacity-50">
              {saving ? "Envoi…" : "Envoyer la tâche"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
